import { useCallback, useEffect, useMemo, useState } from "react";
import { Logo } from "@/components/brand/Logo";
import { cn } from "@/lib/cn";

/**
 * Phase 5 · Opening Experience
 *
 * The first real content in the investor presentation. Pacing is intentional
 * — each line is given its own moment to breathe before the next arrives.
 *
 * Choreography (≈ 35 seconds end-to-end):
 *   1. Logo eases in.
 *   2. Line 1 fades in, dwells, fades out.
 *   3. Line 2 fades in, dwells, fades out.
 *   4. … through Line 5.
 *   5. Brief pause.
 *   6. Chapter 1 placeholder card fades in and stays.
 *
 * Manual controls (delegated from PresentationView via `interceptorRef`):
 *   - Continue / ArrowRight → next line, or next phase, or next chapter
 *   - Back / ArrowLeft       → previous line, or previous phase
 */
const LINES = [
  "Why is it so difficult to find someone who's looking for the same kind of relationship you are?",
  "That question started HAEVN.",
  "At first, I thought I was trying to solve my own problem.",
  "I wasn't sure if it was personal… or something much larger.",
  "So before building anything, I started listening.",
];

// Per-line dwell duration in milliseconds. Long enough that the line can be
// read calmly without feeling slow. The first (longest) line dwells a little
// longer than the rest.
const DWELL_MS = [6800, 5400, 5800, 6200, 6400];

// Time the screen waits after the last line fades out before the Chapter 1
// title card appears. Communicates intention without dragging.
const FINAL_PAUSE_MS = 1600;

// Length of the fade-out between lines.
const FADE_OUT_MS = 700;

export const OpeningChapter = ({ interceptorRef }) => {
  // Phases:
  //   "intro"   — initial 600ms blank fade-in of the logo
  //   "lines"   — progressive reveal of LINES[lineIndex]
  //   "pause"   — brief blackout between line 5 and the title card
  //   "title"   — the Chapter 1 placeholder card
  const [phase, setPhase] = useState("intro");
  const [lineIndex, setLineIndex] = useState(0);
  // `lineVisible` toggles to false during the fade-out frame just before the
  // next line mounts. Driven manually so manual-advance reuses the same
  // transition as auto-advance.
  const [lineVisible, setLineVisible] = useState(true);

  // ── Auto-pacing ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase === "intro") {
      const t = setTimeout(() => setPhase("lines"), 1200);
      return () => clearTimeout(t);
    }
    if (phase === "lines") {
      const dwell = DWELL_MS[lineIndex] ?? 6000;
      // Start the fade-out a bit before mounting the next line.
      const fadeOutAt = setTimeout(() => setLineVisible(false), dwell);
      const next = setTimeout(() => {
        if (lineIndex < LINES.length - 1) {
          setLineIndex((i) => i + 1);
          setLineVisible(true);
        } else {
          setPhase("pause");
        }
      }, dwell + FADE_OUT_MS);
      return () => {
        clearTimeout(fadeOutAt);
        clearTimeout(next);
      };
    }
    if (phase === "pause") {
      const t = setTimeout(() => setPhase("title"), FINAL_PAUSE_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [phase, lineIndex]);

  // ── Manual navigation interceptor ──────────────────────────────────────
  const advanceLine = useCallback(() => {
    setLineVisible(true);
    if (phase === "intro") {
      setPhase("lines");
      setLineIndex(0);
      return true;
    }
    if (phase === "lines") {
      if (lineIndex < LINES.length - 1) {
        setLineIndex((i) => i + 1);
        return true;
      }
      // Past the last line — skip the pause and go straight to the title card.
      setPhase("title");
      return true;
    }
    if (phase === "pause") {
      setPhase("title");
      return true;
    }
    // phase === "title" — let PresentationView advance to the next chapter.
    return false;
  }, [phase, lineIndex]);

  const retreatLine = useCallback(() => {
    setLineVisible(true);
    if (phase === "title" || phase === "pause") {
      setPhase("lines");
      setLineIndex(LINES.length - 1);
      return true;
    }
    if (phase === "lines") {
      if (lineIndex > 0) {
        setLineIndex((i) => i - 1);
        return true;
      }
      // First line — opening is chapter 0, nothing to retreat to.
      return true;
    }
    if (phase === "intro") {
      return true;
    }
    return false;
  }, [phase, lineIndex]);

  useEffect(() => {
    if (!interceptorRef) return undefined;
    interceptorRef.current = {
      tryAdvance: advanceLine,
      tryRetreat: retreatLine,
    };
    return () => {
      if (interceptorRef.current?.tryAdvance === advanceLine) {
        interceptorRef.current = null;
      }
    };
  }, [interceptorRef, advanceLine, retreatLine]);

  // ── Render ─────────────────────────────────────────────────────────────
  const totalSteps = LINES.length + 1; // 5 lines + title card
  const currentStep = useMemo(() => {
    if (phase === "title") return totalSteps;
    if (phase === "lines") return lineIndex + 1;
    return 0; // intro / pause
  }, [phase, lineIndex, totalSteps]);

  return (
    <section
      data-testid="chapter-opening"
      data-phase={phase}
      data-line-index={phase === "lines" ? lineIndex : undefined}
      className="relative flex w-full min-h-[60vh] flex-col items-center justify-center"
    >
      {/* Soft brand-tinted radial behind the text. Restrained — nearly
          invisible in light mode, slightly more present in dark mode. */}
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 -z-10",
          "[mask-image:radial-gradient(circle_at_center,_black,_transparent_70%)]",
          "opacity-70"
        )}
        data-testid="opening-ambient-gradient"
        style={{
          background:
            "radial-gradient(ellipse at center, hsl(var(--brand) / 0.10) 0%, hsl(var(--brand) / 0.05) 35%, transparent 70%)",
        }}
      />

      {/* Logo establishes presence first, then the line text follows. The
          mark is intentionally small and quiet — brand identity without
          competing for attention. */}
      <div
        data-testid="opening-mark"
        className={cn(
          "absolute top-12 transition-opacity duration-1000 ease-out-expo sm:top-16",
          phase === "intro" ? "opacity-0" : "opacity-50"
        )}
      >
        <Logo size="sm" />
      </div>

      {/* Progressive line reveal. Each line gets its own keyed wrapper so the
          fade-up animation runs every time the line changes. */}
      <div
        className="flex w-full max-w-xl flex-col items-center px-6 text-center sm:px-10"
        data-testid="opening-stage"
      >
        {phase === "intro" || phase === "pause" ? (
          <div className="h-[180px]" aria-hidden="true" />
        ) : phase === "lines" ? (
          <div
            key={`line-${lineIndex}`}
            data-testid="opening-line"
            data-line-index={lineIndex}
            className={cn(
              "text-balance text-lg font-thin tracking-tight text-foreground sm:text-xl md:text-[1.375rem]",
              "leading-[1.6] transition-opacity duration-700 ease-out-expo",
              lineVisible ? "animate-fade-up opacity-100" : "opacity-0"
            )}
            style={{ animationDuration: "900ms" }}
          >
            {LINES[lineIndex]}
          </div>
        ) : (
          <div
            key="title-card"
            data-testid="opening-title-card"
            className="flex flex-col items-center gap-5 animate-fade-up"
            style={{ animationDuration: "900ms" }}
          >
            <span
              data-testid="opening-title-chapter"
              className="haevn-eyebrow"
            >
              Chapter 1
            </span>
            <h1
              data-testid="opening-title-name"
              className="text-balance text-2xl font-thin tracking-tight text-foreground sm:text-3xl md:text-4xl leading-[1.15]"
            >
              HAEVN Labs
            </h1>
            <span
              aria-hidden="true"
              className="mt-3 inline-block h-px w-12 bg-border"
            />
          </div>
        )}
      </div>

      {/* Subtle line-level progress: appears only AFTER the first transition.
          On the first screen there is nowhere to go yet — Apple never shows
          UI before you have entered the experience. */}
      {(phase === "title" || (phase === "lines" && lineIndex > 0)) && (
        <div
          aria-hidden="true"
          data-testid="opening-rail"
          className="absolute bottom-24 left-1/2 h-px w-32 -translate-x-1/2 bg-border/80 animate-fade-in"
        >
          <div
            data-testid="opening-rail-fill"
            className="h-full bg-foreground transition-[width] duration-700 ease-out-expo"
            style={{
              width: `${(currentStep / totalSteps) * 100}%`,
            }}
          />
        </div>
      )}
    </section>
  );
};

export default OpeningChapter;
